import { Dispatch } from 'redux';
import { Environment } from 'react-relay/legacy';
import serverVars from 'server-vars';

import { sessionStorage } from 'dibs-browser-storage';
import { browser } from 'dibs-client-check';
import { PAGE_TYPE, BROWSE_TYPES } from 'dibs-constants/exports/pageTypes';
import { VisitCountModel } from 'dibs-visit-tracking/exports/visitCountModel';
import {
    KEY_LAST_GOOGLE_ONE_TAP_PROMPT,
    KEY_GOOGLE_ONE_TAP_LOCAL_PROMPT_COUNT,
    KEY_GOOGLE_ONE_TAP_SESSION_PROMPT_COUNT,
    KEY_TOTAL_VISIT_COUNT,
    PROMPT_COOLDOWN_COUNT,
    PROMPT_LIMIT,
    KEY_REG_PROMPT_LIMIT,
    KEY_SESSION_COUNT,
    VISITS_PER_SESSION_GOOGLE_ONE_TAP_PROMPT,
} from 'dibs-visit-tracking/exports/visitCountConstants';

import widgetLoader from 'dibs-widget-loader';

import { canVisitorBePromptedLogin } from './helpers/canVisitorBePromptedLogin';
import { wasUserRedirectedByEmailLoginLink } from './helpers/wasUserRedirectedByEmailLoginLink';
import getRelayEnvironment from '../../relayClientEnvironment';
import { actionCreators } from '../../actions';

type GoogleLoginHandlersType =
    typeof import('../../authModal/authentication/socialLogin/googleLoginHandlers');
type HandleSocialLoginType = typeof import('../../authModal/authentication/handleSocialLogin');
type AuthActionsType = typeof import('../../authModal/authentication/authActions');

const loadAuthModalModules = (): Promise<
    [GoogleLoginHandlersType, HandleSocialLoginType, AuthActionsType]
> =>
    Promise.all([
        import(
            /* webpackChunkName: "googleLoginHandlers" */
            '../../authModal/authentication/socialLogin/googleLoginHandlers'
        ),
        import(
            /* webpackChunkName: "handleSocialLogin" */
            '../../authModal/authentication/handleSocialLogin'
        ),
        import(
            /* webpackChunkName: "authActions" */
            '../../authModal/authentication/authActions'
        ),
    ]);

export const promptGoogleOneTap =
    (options?: { environment: Environment; runNextNotification: () => void }) =>
    async (dispatch: Dispatch): Promise<void> => {
        const [{ loginOneTapGoogle }, { handleSocialLogin }, { setAuthResponse }] =
            await loadAuthModalModules(); // Only required because of app-buyer-finding/src/actions/sharedUserStorageActions.js

        const environment = options?.environment || getRelayEnvironment();
        const visitCountModel = new VisitCountModel();
        const ga = { label: 'google one tap' };

        await widgetLoader.load('google', { relayEnvironment: environment });

        await loginOneTapGoogle({
            onSuccess: payload => {
                dispatch(setAuthResponse(payload));
                handleSocialLogin({
                    isGdprApplicable: payload.isGdprApplicable || false,
                    attentiveSmsFF: payload.attentiveSmsFF || false,
                    promotionalSmsOptinFF: payload.promotionalSmsOptinFF || false,
                    // @ts-ignore TS doesn't recognize userId under Pick type
                    payload: {
                        ...payload.userInfo,
                        type: payload.type,
                        socialLogin: 'GOOGLE',
                        environment,
                    },
                    launchOrigin: ga.label,
                });
            },
            onError: () => {},
            onDisplayMoment: promptMoment => {
                if (promptMoment.isDisplayed()) {
                    visitCountModel.increment(KEY_GOOGLE_ONE_TAP_LOCAL_PROMPT_COUNT);
                    visitCountModel.set(
                        KEY_LAST_GOOGLE_ONE_TAP_PROMPT,
                        visitCountModel.get(KEY_TOTAL_VISIT_COUNT)
                    );

                    const googleOneTapSessionCount = (sessionStorage.getItem(
                        KEY_GOOGLE_ONE_TAP_SESSION_PROMPT_COUNT
                    ) || 0) as number;
                    sessionStorage.setItem(
                        KEY_GOOGLE_ONE_TAP_SESSION_PROMPT_COUNT,
                        googleOneTapSessionCount + 1
                    );
                    dispatch(actionCreators.setGoogleOneTapVisibility(true));
                } else {
                    dispatch(actionCreators.setGoogleOneTapVisibility(false));
                    options?.runNextNotification();
                }

                visitCountModel.save();
            },
            onSkippedMoment: () => {
                dispatch(actionCreators.setGoogleOneTapVisibility(false));
            },
            environment,
            ga,
            name: 'google',
            marketingOptIn: false,
        });
    };

/**
 * Checks condition for auto prompt user.
 * Opens google oner tap promt if condition is satisfied.
 * Condition:
 * page view >= DISPLAY_VISIT,
 * home / browse / creator / pdp (not print) page type,
 * page is not loaded from EmailTokenLogin email,
 */
export const shouldPromptGoogleOneTap = (): boolean => {
    const visitCountModel = new VisitCountModel();

    const oneTapLocalPromptCount = visitCountModel.get(KEY_GOOGLE_ONE_TAP_LOCAL_PROMPT_COUNT);

    const shouldAutoLoginBePrompted =
        visitCountModel.get(KEY_SESSION_COUNT) === visitCountModel.get(KEY_REG_PROMPT_LIMIT);

    const sinceOneTapSessionPrompt =
        sessionStorage.getItem(KEY_GOOGLE_ONE_TAP_SESSION_PROMPT_COUNT) || 0;
    const isOneTapSessionCooldown = (sinceOneTapSessionPrompt as number) >= PROMPT_COOLDOWN_COUNT;

    const wasRedirectedByEmailLoginLink = wasUserRedirectedByEmailLoginLink();

    const isMobile = serverVars.get('settings.isMobile');
    const isMobileFirefoxSafari = isMobile && (browser.mozilla() || browser.safari());
    const isMobileOneTapSessionInitialCooldown =
        isMobile &&
        visitCountModel.get(KEY_SESSION_COUNT) < VISITS_PER_SESSION_GOOGLE_ONE_TAP_PROMPT;

    if (isMobileFirefoxSafari) {
        return false;
    }

    if (isMobileOneTapSessionInitialCooldown) {
        return false;
    }

    if (oneTapLocalPromptCount >= PROMPT_LIMIT || !canVisitorBePromptedLogin()) {
        return false;
    }

    if (shouldAutoLoginBePrompted) {
        return false;
    }

    if (isOneTapSessionCooldown) {
        return false;
    }

    if (wasRedirectedByEmailLoginLink) {
        return false;
    }

    return true;
};

export default {
    name: 'googleOneTapPrompt',
    rule: shouldPromptGoogleOneTap,
    action: promptGoogleOneTap,
    allowedPageTypes: [
        PAGE_TYPE.HOME,
        PAGE_TYPE.CREATOR,
        PAGE_TYPE.DEALER,
        PAGE_TYPE.PDP,
        PAGE_TYPE.SEARCH,
        ...BROWSE_TYPES,
    ],
    priority: 360,
};
